import type { FieldDefinitions } from "@/components/forms/types";

/**
 * Field definitions for the "Become a DAS Centre" registration form: the port
 * of poppy_get_das_academy_registration_form_fields() in includes/forms.php.
 *
 * The form is multi-step, so every field carries a step (1 = Centre Approval,
 * 2 = Your Details). The field name is also the input's name and, on
 * WordPress, the ACF field name the handler stored it under; select and radio
 * answers go in as their slug keys.
 *
 * This is the only description of the form. The fields are rendered from it
 * and the rules in components/forms/validate.ts are run against it, and a
 * future backend should validate against the same definitions.
 */

const ORGANISATION_TYPES: Record<string, string> = {
  further_education_college: "Further Education College",
  sixth_form_college: "Sixth Form College",
  university_higher_education: "University / Higher Education",
  university_technical_college: "University Technical College",
  school_academy: "School / Academy",
  independent_training_provider: "Independent Training Provider",
  local_authority: "Local Authority / Combined Authority",
  employer_training_centre: "Employer Training Centre",
  other: "Other",
};

const ROLES: Record<string, string> = {
  principal_chief_executive: "Principal / Chief Executive",
  deputy_vice_principal: "Deputy / Vice Principal",
  director: "Director",
  curriculum_lead: "Curriculum Lead",
  head_of_department: "Head of Department",
  careers_employability_lead: "Careers / Employability Lead",
  partnerships_employer_engagement: "Partnerships / Employer Engagement",
  estates_facilities: "Estates / Facilities",
  lecturer_tutor: "Lecturer / Tutor",
  other: "Other",
};

/** proposed_location's answer when the applicant's area isn't one of the proposed locations. */
export const SUGGEST_LOCATION = "suggest";

const ACADEMY_FORMATS: Record<string, string> = {
  drive_through: "Drive-Through Academy",
  campus: "Campus Academy",
  compact: "Compact Academy",
  mobility_hub: "Mobility Hub",
  not_sure: "Not sure yet",
};

export const REGISTRATION_FIELDS: FieldDefinitions = {
  // Step 1 - Centre Approval.
  accepted_standards: {
    step: 1,
    group: "DAS Centre Standards",
    label: "Please confirm that you understand and accept these requirements",
    type: "radio",
    options: { yes: "Yes, I accept", no: "No" },
    validation: {
      required: true,
      type: "radio",
      options: { yes: "Yes, I accept", no: "No" },
      equals: "yes",
      messages: {
        required: "Please confirm your acceptance to continue",
        equals:
          "Centre approval requires acceptance of the DAS Centre Standards. Please contact the DAS team if you would like to discuss these requirements before registering.",
      },
    },
  },
  recognised_provider: {
    step: 1,
    group: "Organisation eligibility",
    label:
      "Is your organisation a recognised education, training or skills provider?",
    type: "radio",
    options: { yes: "Yes", no: "No" },
    validation: {
      required: true,
      type: "radio",
      options: { yes: "Yes", no: "No" },
      equals: "yes",
      messages: {
        required: "Please indicate your organisation type to continue",
        // The group's note already says who approval is intended for; this is what to do about it.
        equals:
          "Please contact DAS if you believe your organisation should be considered.",
      },
    },
  },

  // Step 2 - Your Details: personal details.
  first_name: {
    step: 2,
    group: "Personal details",
    label: "First name",
    type: "text",
    validation: {
      required: true,
      type: "text",
      messages: { required: "Enter your first name" },
    },
  },
  last_name: {
    step: 2,
    group: "Personal details",
    label: "Last name",
    type: "text",
    validation: {
      required: true,
      type: "text",
      messages: { required: "Enter your last name" },
    },
  },
  work_email: {
    step: 2,
    group: "Personal details",
    label: "Work email",
    type: "email",
    placeholder: "name@organisation.ac.uk",
    validation: {
      required: true,
      type: "email",
      messages: {
        required: "Enter your work email address",
        email: "Enter a valid work email address",
      },
    },
  },
  phone: {
    step: 2,
    group: "Personal details",
    label: "Phone number",
    type: "tel",
    placeholder: "e.g. 01234 567 890",
    validation: {
      required: true,
      type: "text",
      messages: { required: "Enter your phone number" },
    },
  },

  // Step 2 - Your Details: organisation.
  organisation_name: {
    step: 2,
    group: "Your organisation",
    label: "Organisation name",
    type: "text",
    help_text:
      "Enter the full recognised or legal name of your college or organisation.",
    validation: {
      required: true,
      type: "text",
      messages: { required: "Enter your organisation name" },
    },
  },
  organisation_type: {
    step: 2,
    group: "Your organisation",
    label: "Organisation type",
    type: "select",
    options: ORGANISATION_TYPES,
    validation: {
      required: true,
      type: "select",
      options: ORGANISATION_TYPES,
      messages: {
        required: "Select your organisation type",
        options: "Select your organisation type",
      },
    },
  },
  organisation_type_other: {
    step: 2,
    group: "Your organisation",
    label: "Please specify organisation type",
    type: "text",
    validation: {
      required: false,
      type: "text",
      required_if: { field: "organisation_type", value: "other" },
      messages: { required_if: "Specify your organisation type" },
    },
  },
  role: {
    step: 2,
    group: "Your organisation",
    label: "Your role",
    type: "select",
    options: ROLES,
    validation: {
      required: true,
      type: "select",
      options: ROLES,
      messages: { required: "Select your role", options: "Select your role" },
    },
  },
  role_other: {
    step: 2,
    group: "Your organisation",
    label: "Please specify your role",
    type: "text",
    validation: {
      required: false,
      type: "text",
      required_if: { field: "role", value: "other" },
      messages: { required_if: "Specify your role" },
    },
  },
  ukprn: {
    step: 2,
    group: "Your organisation",
    label: "UK Provider Reference Number (UKPRN)",
    type: "text",
    placeholder: "12345678",
    help_text: "Enter your organisation's eight-digit UKPRN.",
    disclosure: {
      title: "What is a UKPRN?",
      text: "A UK Provider Reference Number (UKPRN) is a unique eight-digit number allocated by the UK Register of Learning Providers (UKRLP). All recognised UK education and training providers are listed on the UKRLP at ukrlp.co.uk.",
    },
    validation: {
      required: true,
      type: "ukprn",
      messages: {
        required: "Enter your eight-digit UKPRN",
        ukprn: "Enter a valid eight-digit UKPRN",
      },
    },
  },

  // Step 2 - Your Details: organisation address.
  address_line_1: {
    step: 2,
    group: "Organisation address",
    label: "Address line 1",
    type: "text",
    validation: {
      required: true,
      type: "text",
      messages: { required: "Enter the first line of your address" },
    },
  },
  address_line_2: {
    step: 2,
    group: "Organisation address",
    label: "Address line 2",
    type: "text",
    validation: { required: false, type: "text" },
  },
  town_city: {
    step: 2,
    group: "Organisation address",
    label: "Town / City",
    type: "text",
    validation: {
      required: true,
      type: "text",
      messages: { required: "Enter your town or city" },
    },
  },
  county: {
    step: 2,
    group: "Organisation address",
    label: "County",
    type: "text",
    validation: { required: false, type: "text" },
  },
  postcode: {
    step: 2,
    group: "Organisation address",
    label: "Postcode",
    type: "text",
    validation: {
      required: true,
      type: "text",
      messages: { required: "Enter your postcode" },
    },
  },
  country: {
    step: 2,
    group: "Organisation address",
    label: "Country",
    type: "text",
    value: "United Kingdom",
    validation: { required: false, type: "text" },
  },

  // Step 2 - Your Details: proposed DAS Academy.
  // One of the published academy_location slugs, or "suggest". The options come from the database
  // (LocationSelect), so the server checks the slug itself (features/centre-registrations/submit.ts).
  proposed_location: {
    step: 2,
    group: "Your proposed DAS Academy",
    label: "DAS Academy location",
    type: "select",
    help_text:
      "Choose one of our proposed locations, or tell us about your own area.",
    validation: {
      required: true,
      type: "text",
      messages: {
        required:
          "Choose a proposed location, or choose to suggest your own area",
      },
    },
  },
  suggested_area: {
    step: 2,
    group: "Your proposed DAS Academy",
    label: "Town or area you are proposing",
    type: "text",
    placeholder: "e.g. Wigan",
    validation: {
      required: false,
      type: "text",
      required_if: { field: "proposed_location", value: SUGGEST_LOCATION },
      messages: { required_if: "Enter the town or area you are proposing" },
    },
  },
  academy_format: {
    step: 2,
    group: "Your proposed DAS Academy",
    label: "Academy format you have in mind",
    type: "select",
    options: ACADEMY_FORMATS,
    validation: { required: false, type: "select", options: ACADEMY_FORMATS },
  },
  academy_location: {
    step: 2,
    group: "Your proposed DAS Academy",
    label: "Site, campus or premises you have in mind",
    type: "text",
    placeholder: "e.g. Main Campus, Barnsley College",
    help_text: "If you already have premises in mind, tell us where.",
    validation: { required: false, type: "text" },
  },
  academy_objective: {
    step: 2,
    group: "What would you like to achieve?",
    label:
      "Please tell us briefly what you would like a DAS Academy to achieve for your learners",
    type: "paragraph",
    help_text:
      "A few sentences is enough. We will explore this with you during the introductory discussion.",
    max_length: 750,
    validation: {
      required: true,
      type: "paragraph",
      max_length: 750,
      messages: {
        required:
          "Tell us briefly what you would like your DAS Academy to achieve",
      },
    },
  },
};

/*
 * Two things the field definitions deliberately do not carry are decided here,
 * because they are presentation rather than data: which fields sit two to a
 * row, and which autofill token each field takes. Both are keyed by field name
 * and default to something sensible, so a new field renders full width with no
 * autofill rather than not rendering at all.
 */

/** Presentation only: fields that sit two to a row on wider screens. */
export const HALF_WIDTH_FIELDS = [
  "first_name",
  "last_name",
  "work_email",
  "phone",
  "organisation_type",
  "organisation_type_other",
  "role",
  "role_other",
  "town_city",
  "county",
  "postcode",
  "country",
];

/** Presentation only: the autofill token each field accepts. */
export const AUTOCOMPLETE_TOKENS: Record<string, string> = {
  first_name: "given-name",
  last_name: "family-name",
  work_email: "email",
  phone: "tel",
  organisation_name: "organization",
  role: "organization-title",
  address_line_1: "address-line1",
  address_line_2: "address-line2",
  town_city: "address-level2",
  county: "address-level1",
  postcode: "postal-code",
  country: "country-name",
};

/**
 * Each step introduces itself, inside itself. A step's heading sits under the
 * progress nav and inside the step it belongs to, so it goes when the step
 * goes. Line breaks in the text become separate paragraphs.
 */
export const STEP_INTROS: Record<number, { title: string; text: string }> = {
  1: {
    title: "Thank you for your interest in becoming an approved DAS Centre.",
    text: "To deliver DAS Driver Education and operate a DAS Academy, your organisation will first need to become an approved DAS Centre.\nPlease complete the sections below. A member of the DAS team will contact you to discuss your organisation, understand what you would like to achieve and support you with the next steps.",
  },
  2: {
    title: "Tell us about you and your organisation",
    text: "We only need a few details to begin your DAS Centre registration.",
  },
};

/**
 * The note that opens a group, above its first field. Both eligibility gates
 * are questions nobody can answer without being told what they are agreeing
 * to, so each one is prefaced rather than only the first.
 */
export const GROUP_NOTES: Record<string, string> = {
  "DAS Centre Standards":
    "DAS Centre approval is subject to your organisation meeting the requirements of the DAS Centre Standards and completing the appropriate organisational and due diligence checks.",
  "Organisation eligibility":
    "DAS Academy approval is currently intended for recognised education, training and skills organisations.",
  "Your proposed DAS Academy":
    "Tell us where you are considering establishing your DAS Academy.",
};

export const STEP_LABELS = ["Centre Approval", "Your Details"];
